import { useMemo } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { catalogQueryString } from '../models/catalog-query.model';
import { routes } from '../../assets/constants';
import { useAuthContext } from './use-auth-context.hook';
import { requestListQueryString } from '../../modules/vendor/shopper-requests/request-format';
import type {
    NextStepSettings,
    RequestDetail,
    RequestList,
    RequestListQuery,
    RequestPatch,
    ShopperRequestSettings,
    StoreImportResult,
    VendorStore,
} from '../../modules/vendor/shopper-requests/types';

export * from '../../modules/vendor/shopper-requests/types';

const BASE = '/services/pms/shopper-requests';

/**
 * Shopper requests (workstream A2): the vendor's inbox of samples, visits,
 * quotes, call-backs and orders, the next-step settings, the store list —
 * and the catalogue page the showroom QR sheet picks products from.
 */
export const useShopperRequestsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    return useMemo(
        () => ({
            list: async (query: RequestListQuery = {}): Promise<RequestList> =>
                (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}${requestListQueryString(query)}`)) as RequestList,

            get: async (uuid: string): Promise<RequestDetail> =>
                (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/${encodeURIComponent(uuid)}`)) as RequestDetail,

            patch: async (uuid: string, body: RequestPatch): Promise<RequestDetail> =>
                (await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/${encodeURIComponent(uuid)}`, body)) as RequestDetail,

            getSettings: async (): Promise<ShopperRequestSettings> =>
                (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/settings`)) as ShopperRequestSettings,

            saveSettings: async (body: { nextSteps?: NextStepSettings; stores?: VendorStore[] }): Promise<ShopperRequestSettings> =>
                (await sendAuthGuardedRequest(ApiMethod.PUT, `${BASE}/settings`, body)) as ShopperRequestSettings,

            importStores: async (): Promise<StoreImportResult> =>
                (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/stores/import`)) as StoreImportResult,

            /**
             * One page of the vendor's visible, complete products, as the API
             * returns them — raw, so `commerce.price` comes through (the
             * catalogue's Product class drops it).
             */
            listProducts: async (request: { search?: string; page?: number; limit?: number }): Promise<{ data: any[]; total: number }> => {
                const response: any = await sendAuthGuardedRequest(
                    ApiMethod.GET,
                    routes.products.findAll +
                        catalogQueryString({ page: request.page ?? 1, limit: request.limit ?? 48, search: request.search, draft: 'complete', visibility: 'visible', order: 'NAME_ASC' }),
                );
                return { data: response?.data ?? [], total: Number(response?.total) || 0 };
            },
        }),
        [sendAuthGuardedRequest],
    );
};
